import { GameState } from '../systems/GameState';
import { GameData } from '../data/GameData';
import { AvatarConfig } from '../avatar/config';
import { avatarPngSrc, hasAvatarRender, AVATAR_MASTER_PX } from '../avatar/gamemount';
import { ASSISTANT_AVATAR } from '../data/Assistant';
import { DialogLine, DialogSpeaker } from './VnDialog';

/**
 * 说话人出图（2026-10-13 收口）：原先四条演出通道各写一遍「查 `player.normalNpcs` → `avatarPngSrc` → 挂 `avatarSrc`」，
 * 结果固定 NPC（`NPCDef.avatar`，许林辉首个）四条全漏——偶遇、探班、剧情、颁奖夜都只剩一枚首字色块。
 * 现在一律走这里：随机 NPC 取档内实例那份脸，固定 NPC 取主表定稿那份，主角取当前穿着。
 * 规矩同 `FreeroamMap.withNpcAvatar` 当年那条（2026-10-12 茄子令修「探班只有个姓、没有脸」）：⛔ 再各写一遍。
 */

/** 这个说话人的捏脸配置；取不到（未入表/旁白态/无名角色）＝null，留首字色块兜底 */
export function npcAvatarConfig(speakerId: string): AvatarConfig | null {
    const player = GameState.getInstance().player;
    if (speakerId === 'player') return hasAvatarRender(player.avatar) ? player.avatar : null;
    // 小助理不在 NPC 主表里（她是 data/Assistant.ts 的固定形象），但她的脸走同一条捏脸管线
    if (speakerId === 'assistant') return hasAvatarRender(ASSISTANT_AVATAR) ? ASSISTANT_AVATAR : null;
    const cfg = player.normalNpcs?.find(n => n.id === speakerId)?.avatar
        ?? GameData.NPC_MAP[speakerId]?.avatar
        ?? null;
    return cfg && hasAvatarRender(cfg) ? cfg : null;
}

/** 单个说话人：出好图（或压根没脸）再回调；出图失败也回调，⛔ 卡住演出 */
export function withSpeakerAvatar(speaker: DialogSpeaker, onReady: () => void): void {
    applyTo(speaker, onReady);
}

/** 一整段台词：把段内每位说话人的脸都出齐再回调（剧情一拍里可以有四五位） */
export function withLineAvatars(lines: DialogLine[], onReady: () => void): void {
    const todo = new Set<DialogSpeaker>();
    for (const l of lines) {
        if (l.speaker.avatarSrc || l.speaker.noAvatar || l.speaker.isPlayer) continue;
        todo.add(l.speaker);
        if (l.choices) for (const c of l.choices) {
            const replies = c.reply ? (Array.isArray(c.reply) ? c.reply : [c.reply]) : [];
            replies.forEach(r => { if (r.speaker && !r.speaker.avatarSrc && !r.speaker.noAvatar && !r.speaker.isPlayer) todo.add(r.speaker); });
        }
    }
    if (!todo.size) { onReady(); return; }
    let left = todo.size;
    const done = () => { if (--left <= 0) onReady(); };
    todo.forEach(sp => applyTo(sp, done));
}

function applyTo(speaker: DialogSpeaker, onReady: () => void): void {
    if (speaker.avatarSrc) { onReady(); return; }
    const cfg = npcAvatarConfig(speaker.id);
    if (!cfg) { onReady(); return; }
    avatarPngSrc(cfg, AVATAR_MASTER_PX)
        .then(src => { speaker.avatarSrc = src; onReady(); })
        .catch(onReady);
}
